import { useMemo } from 'react';
import { getPropertyValue, AVAILABLE_PROPERTIES } from './matrixClassification';

/**
 * Derive available properties from actual feature data.
 *
 * Currently scans feature metadata client-side to determine which
 * properties have data, then returns only the matching entries from
 * AVAILABLE_PROPERTIES.
 *
 * For DSP features with multi-class detection models, this hook also
 * discovers dynamic per-class hectare equivalent properties (keys matching
 * 'hectare_equivalent:<className>') and appends them to the result.
 * When per-class options exist, the total is relabeled "Ha/eq (Total)"
 * to distinguish it from per-class entries like "Ha/eq (Tall)".
 *
 * ## API Integration (Requirement 6)
 *
 * When the backend implements Requirement 6, the hexagon features
 * response (POST /projects/{projectId}/hexagons/{taskId}/{resolution})
 * will include an `available_properties` array:
 *
 * ```json
 * {
 *   "hexagon_data": { ... },
 *   "available_properties": [
 *     { "id": "mean_height", "label": "Mean Height", "unit": "m" },
 *     { "id": "count", "label": "Count", "unit": "pcs" }
 *   ]
 * }
 * ```
 *
 * To integrate:
 * 1. Add `hexagonInfo` as a parameter to this hook
 * 2. If `hexagonInfo.available_properties` exists, return it directly
 * 3. Fall back to the current client-side scanning when the field is absent
 * 4. Remove the client-side scanning once all environments return the field
 *
 * @param {Array} features - Array of OpenLayers features (hexagons or DSP plots)
 * @returns {Array<{id: string, label: string, unit: string}>}
 */
export default function useAvailableProperties(features) {
	// TODO: When backend ships Requirement 6, accept hexagonInfo param and
	// return hexagonInfo.available_properties when present, e.g.:
	//
	// export default function useAvailableProperties(features, hexagonInfo) {
	//   if (hexagonInfo?.available_properties?.length) {
	//     return hexagonInfo.available_properties;
	//   }
	//   // ...fall back to client-side scanning below
	// }

	return useMemo(() => {
		if (!features?.length) return AVAILABLE_PROPERTIES;

		// Sample up to 20 features to detect which properties exist.
		// This client-side scanning will be replaced by the API response.
		const sampleSize = Math.min(features.length, 20);
		const foundIds = new Set();
		const dynamicProperties = [];
		const foundDynamic = new Set();

		for (let i = 0; i < sampleSize; i++) {
			const meta = features[i].values_?.data?.metadata;
			if (!meta) continue;

			// Check static properties
			for (const prop of AVAILABLE_PROPERTIES) {
				if (foundIds.has(prop.id)) continue;
				const val = getPropertyValue(meta, prop.id);
				if (val !== null && !isNaN(val)) {
					foundIds.add(prop.id);
				}
			}

			// Discover dynamic per-class hectare equivalent properties.
			// These are flattened into metadata during DSP enrichment as
			// 'hectare_equivalent:<className>' keys.
			for (const key of Object.keys(meta)) {
				if (key.startsWith('hectare_equivalent:') && !foundDynamic.has(key)) {
					const val = meta[key];
					if (val != null && !isNaN(val)) {
						const className = key.slice('hectare_equivalent:'.length);
						dynamicProperties.push({
							id: key,
							label: `Ha/eq (${className})`,
							unit: '/ha',
						});
						foundDynamic.add(key);
					}
				}
			}
		}

		// Build result: static properties with data + dynamic per-class properties
		const staticResult =
			foundIds.size === 0
				? AVAILABLE_PROPERTIES
				: AVAILABLE_PROPERTIES.filter(p => foundIds.has(p.id));

		// Rename the total hectare_equivalent label if per-class options exist
		if (dynamicProperties.length > 0) {
			dynamicProperties.sort((a, b) => a.label.localeCompare(b.label));
			const result = staticResult.map(p =>
				p.id === 'hectare_equivalent'
					? { ...p, label: 'Ha/eq (Total)' }
					: p
			);
			return [...result, ...dynamicProperties];
		}

		return staticResult;
	}, [features]);
}
